import type { FC, KeyboardEvent } from 'react';
import React, { useState, useCallback, useRef } from 'react';
import classNames from 'clsx';

import { ICON_SIZE_MAP, PLAYBACK_RATES } from '../../../constants';
import { PlaybackIcon } from '../../assets/PlaybackIcon';
import { CheckmarkIcon } from '../../assets/CheckmarkIcon';
import { Popover } from '../Popover/Popover';
import { Tooltip } from '../Tooltip/Tooltip';

import styles from './PlaybackRate.module.scss';

type PlaybackRateProps = {
  size: 'medium' | 'large';
  playbackRate: number;
  onPlaybackRateChange: (playbackRate: number) => void;
};

export const PlaybackRate: FC<PlaybackRateProps> = ({
  size,
  playbackRate,
  onPlaybackRateChange,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const tooltipContent = 'Playback speed';
  const ariaLabel = 'Playback speed';
  const menuAriaLabel = 'Playback speed options';
  const iconSize = ICON_SIZE_MAP[size];

  const handleOpen = useCallback(() => {
    setIsPopoverOpen(true);
    requestAnimationFrame(() => {
      itemRefs.current[0]?.focus();
    });
  }, []);

  const handleClose = useCallback(() => {
    setIsPopoverOpen(false);
    triggerRef.current?.focus();
  }, []);

  const togglePopover = useCallback(() => {
    if (isPopoverOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isPopoverOpen, handleClose, handleOpen]);

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      onPlaybackRateChange(rate);
      handleClose();
    },
    [onPlaybackRateChange, handleClose],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isPopoverOpen) {
        return;
      }

      const focusableElements = itemRefs.current;
      const currentIndex = focusableElements.findIndex(
        element => element === document.activeElement,
      );

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          handleClose();
          break;

        case 'ArrowDown':
          event.preventDefault();
          event.stopPropagation();
          if (focusableElements.length === 0) {
            return;
          }
          const nextDownIndex =
            currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
          focusableElements[nextDownIndex]?.focus();
          break;

        case 'ArrowUp':
          event.preventDefault();
          event.stopPropagation();
          if (focusableElements.length === 0) {
            return;
          }
          const nextUpIndex =
            currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
          focusableElements[nextUpIndex]?.focus();
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          event.stopPropagation();
          const activeElement = document.activeElement as HTMLButtonElement;
          activeElement?.click();
          break;

        case 'Tab':
          event.preventDefault();
          if (focusableElements.length === 0) {
            return;
          }

          let nextIndex;
          if (event.shiftKey) {
            nextIndex =
              currentIndex <= 0
                ? focusableElements.length - 1
                : currentIndex - 1;
          } else {
            nextIndex =
              currentIndex >= focusableElements.length - 1
                ? 0
                : currentIndex + 1;
          }

          focusableElements[nextIndex]?.focus();
          break;
      }
    },
    [isPopoverOpen, handleClose],
  );

  return (
    <Popover
      triggerRef={triggerRef}
      trigger={
        <Tooltip content={tooltipContent} disabled={isPopoverOpen}>
          <button
            ref={triggerRef}
            className={styles.button}
            type="button"
            tabIndex={0}
            aria-label={ariaLabel}
            aria-expanded={isPopoverOpen}
            aria-haspopup="menu"
            onClick={togglePopover}
          >
            <PlaybackIcon width={iconSize} height={iconSize} />
          </button>
        </Tooltip>
      }
      isOpen={isPopoverOpen}
      onClose={handleClose}
    >
      <div
        className={styles.popoverItemsContainer}
        role="menu"
        aria-orientation="vertical"
        aria-label={menuAriaLabel}
        onKeyDown={handleKeyDown}
      >
        {PLAYBACK_RATES.map((rate, index) => {
          const isSelected = rate === playbackRate;

          return (
            <button
              key={rate}
              ref={el => (itemRefs.current[index] = el)}
              className={classNames(styles.popoverItem, {
                [styles.selected]: isSelected,
              })}
              type="button"
              tabIndex={0}
              role="menuitem"
              aria-current={isSelected ? 'true' : undefined}
              aria-label={`Playback speed ${rate}x`}
              onClick={() => handlePlaybackRateChange(rate)}
            >
              <div className={styles.checkedIcon} aria-hidden="true">
                {isSelected && <CheckmarkIcon />}
              </div>
              <div>{rate}</div>
            </button>
          );
        })}
      </div>
    </Popover>
  );
};
